import { useMemo } from 'react';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { Button, Link, Stack } from '@mui/material';
import MaterialReactTable from 'material-react-table';
import EvidenceBadge from './EvidenceBadge';
import InfoLabel from './InfoLabel';
import {
  formatAppearance,
  formatDetectionPct,
  formatEndothelialContextFraction,
  formatScientificValue,
} from '../../utils/formatters';
import { buildExportFilename, downloadCsv } from '../../utils/csv';

const fullTissues = [
  'Cornea',
  'Heart',
  'Liver',
  'Umbilical vein',
  'Lymph node',
  'Sciatic nerve',
  'Colon',
  'Vessel',
  'Foreskin',
  'Peripheral blood',
  'Lung',
  'Skin',
  'Kidney',
  'Intestine',
  'Tonsil',
];

const selectedTissues = ['Colon', 'Heart', 'Intestine', 'Kidney', 'Liver', 'Lung', 'Vessel'];

const transmembraneFocusExportFields = [
  'Name',
  'gene_names',
  'uniprot',
  'Score',
  'lists_represented',
  'brain_median_mrn',
  'brain_detection_pct',
  'endothelial_context_fraction',
  'DESeq2_Appeared',
  'DESeq2_Validated',
  'Wilcox_Appeared',
  'Wilcox_Validated',
  'Prot_Evidence',
  'mean_perf_score',
];

export function projectRowsForExport(rows, kind, selection) {
  const tissues = selection?.scope === 'subset' ? selectedTissues : fullTissues;
  const fields = kind === 'transmembraneFocus'
    ? transmembraneFocusExportFields
    : [
      'Gene_ID',
      'hgncSymbol',
      'sigAdj',
      ...(selection.method === 'deseq2' ? ['log2FoldChange'] : []),
      'brain_median_mrn',
      'brain_detection_pct',
      'endothelial_context_fraction',
      'appCount',
      ...tissues,
    ];

  return rows.map((row) => Object.fromEntries(
    fields.map((field) => [field, row[field]]),
  ));
}

function technicalHeader(label, description) {
  return () => <InfoLabel label={label} description={description} />;
}

function numericColumn(accessorKey, label, description, formatter = formatScientificValue) {
  return {
    accessorKey,
    header: label,
    Header: technicalHeader(label, description),
    Cell: ({ cell }) => formatter(cell.getValue()),
    muiTableBodyCellProps: { sx: { fontVariantNumeric: 'tabular-nums' } },
  };
}

function geneColumns(kind, onExpression) {
  const idKey = kind === 'transmembraneFocus' ? 'Name' : 'Gene_ID';
  const nameKey = kind === 'transmembraneFocus' ? 'gene_names' : 'hgncSymbol';

  return [
    {
      accessorKey: idKey,
      header: 'Gene ID',
      enableClickToCopy: true,
      size: 168,
    },
    {
      accessorKey: nameKey,
      header: 'Gene name',
      enableClickToCopy: false,
      Cell: ({ row }) => {
        const name = row.original[nameKey];
        return name ? (
          <Link
            href={`https://www.proteinatlas.org/search/${encodeURIComponent(name)}`}
            target="_blank"
            rel="noreferrer"
          >
            {name}
          </Link>
        ) : '—';
      },
      size: 130,
    },
    {
      id: 'expression',
      header: 'Expression',
      accessorFn: (row) => row[idKey],
      enableSorting: false,
      enableColumnFilter: false,
      Cell: ({ row }) => {
        const id = row.original[idKey];
        const name = row.original[nameKey] || id;
        return (
          <Button
            size="small"
            variant="outlined"
            startIcon={<VisibilityOutlinedIcon />}
            aria-label={`View expression for ${name}`}
            onClick={() => onExpression(id, name)}
          >
            View
          </Button>
        );
      },
      size: 120,
    },
  ];
}

function transmembraneFocusColumns(onExpression) {
  const [geneId, geneName, expression] = geneColumns('transmembraneFocus', onExpression);
  const evidenceColumn = (accessorKey, label, description) => ({
    accessorKey,
    header: label,
    Header: technicalHeader(label, description),
    enableSorting: false,
    filterVariant: 'checkbox',
    Cell: ({ cell }) => <EvidenceBadge value={cell.getValue()} context={label} />,
    size: 150,
  });

  return [
    geneId,
    geneName,
    {
      accessorKey: 'uniprot',
      header: 'UniProt',
      Cell: ({ cell }) => cell.getValue() ? (
        <Link
          href={`https://www.ebi.ac.uk/interpro/search/text/${encodeURIComponent(cell.getValue())}`}
          target="_blank"
          rel="noreferrer"
        >
          {cell.getValue()}
        </Link>
      ) : '—',
    },
    numericColumn(
      'Score',
      'Four-list RRA score',
      'Negative log10 of the Robust Rank Aggregation p-value across four Brain-overexpression lists. Higher values indicate stronger prioritization in the four-list RRA ranking.',
    ),
    {
      accessorKey: 'lists_represented',
      header: 'Lists represented',
      Header: technicalHeader(
        'Lists represented',
        'Number of displayed RRA results containing the gene: DESeq2 aggregate, DESeq2 bootstrap validation, WRST aggregate, and WRST bootstrap validation.',
      ),
      filterVariant: 'select',
      filterSelectOptions: [
        { text: '0/4', value: 0 },
        { text: '1/4', value: 1 },
        { text: '2/4', value: 2 },
        { text: '3/4', value: 3 },
        { text: '4/4', value: 4 },
      ],
      Cell: ({ cell }) => {
        const value = cell.getValue();
        return value === null || value === undefined || !Number.isInteger(Number(value))
          ? '—'
          : `${Number(value)}/4`;
      },
      muiTableBodyCellProps: { sx: { fontVariantNumeric: 'tabular-nums' } },
      size: 150,
    },
    numericColumn(
      'brain_median_mrn',
      'Brain median count',
      'Median untransformed MRN-normalized count across the 37 Brain samples.',
    ),
    numericColumn(
      'brain_detection_pct',
      'Brain detection',
      'Percentage of the 37 Brain samples with an MRN-normalized count greater than zero.',
      formatDetectionPct,
    ),
    numericColumn(
      'endothelial_context_fraction',
      'Endothelial context fraction',
      'Fraction of available non-Brain HPA endothelial tissues where Brain capillary expression is at least 1 nCPM and at least fourfold the largest eligible endothelial-cluster value.',
      formatEndothelialContextFraction,
    ),
    evidenceColumn(
      'DESeq2_Appeared',
      'DESeq2 aggregate',
      'Membership in the significant method-specific RRA result derived from DESeq2-ranked comparisons (RRA p < 0.05).',
    ),
    evidenceColumn(
      'DESeq2_Validated',
      'DESeq2 bootstrap validation',
      'Membership in the DESeq2 bootstrap-validation RRA result after 100 subsampling runs (RRA p < 0.05).',
    ),
    evidenceColumn(
      'Wilcox_Appeared',
      'WRST aggregate',
      'Membership in the significant method-specific RRA result derived from WRST-ranked comparisons (RRA p < 0.05).',
    ),
    evidenceColumn(
      'Wilcox_Validated',
      'WRST bootstrap validation',
      'Membership in the WRST bootstrap-validation RRA result after 100 subsampling runs (RRA p < 0.05).',
    ),
    {
      accessorKey: 'Prot_Evidence',
      header: 'Protein evidence',
      Header: technicalHeader(
        'Protein evidence',
        'Detection in PXD018602 after MaxQuant Q < 0.01 and contaminant/decoy filtering. A negative badge means not detected in that dataset, rather than evidence of protein absence.',
      ),
      enableSorting: false,
      filterVariant: 'checkbox',
      Cell: ({ cell }) => (
        <EvidenceBadge
          value={cell.getValue()}
          context="PXD018602 protein evidence"
          positiveLabel="Detected"
          negativeLabel="Not detected"
        />
      ),
      size: 150,
    },
    numericColumn(
      'mean_perf_score',
      'Perfusion context score',
      'Mean across four analysis configurations of summed perfusion rates for high-perfusion comparator tissues where the gene was significantly Brain-overexpressed. This contextual annotation is not part of the four-list RRA ranking.',
    ),
    expression,
  ];
}

function allGeneColumns(selection, onExpression) {
  const [geneId, geneName, expression] = geneColumns('allGenes', onExpression);
  const columns = [
    geneId,
    geneName,
    numericColumn(
      'sigAdj',
      'Mean significance',
      'Mean negative log10 adjusted p-value across comparisons where the gene was significant under the selected analysis.',
    ),
  ];

  if (selection.method === 'deseq2') {
    columns.push(
      numericColumn(
        'log2FoldChange',
        'Mean log2 fold change',
        'Mean log2 fold change across significant comparisons; Brain is the numerator.',
      ),
    );
  }

  columns.push(
    numericColumn(
      'brain_median_mrn',
      'Brain median count',
      'Median untransformed MRN-normalized count across the 37 Brain samples.',
    ),
    numericColumn(
      'brain_detection_pct',
      'Brain detection',
      'Percentage of the 37 Brain samples with an MRN-normalized count greater than zero.',
      formatDetectionPct,
    ),
    numericColumn(
      'endothelial_context_fraction',
      'Endothelial context fraction',
      'Fraction of available non-Brain HPA endothelial tissues where Brain capillary expression is at least 1 nCPM and at least fourfold the largest eligible endothelial-cluster value.',
      formatEndothelialContextFraction,
    ),
    numericColumn(
      'appCount',
      'Comparison support',
      'Number of tissue comparisons where the gene was significant, shown against the selected comparison total.',
      (value) => formatAppearance(value, selection.scope),
    ),
  );

  const tissues = selection.scope === 'subset' ? selectedTissues : fullTissues;
  for (const tissue of tissues) {
    columns.push({
      accessorKey: tissue,
      header: tissue,
      Header: technicalHeader(
        tissue,
        `Whether this gene met the selected differential-expression criterion in the Brain versus ${tissue} comparison.`,
      ),
      enableSorting: false,
      filterVariant: 'checkbox',
      Cell: ({ cell }) => (
        <EvidenceBadge
          value={cell.getValue()}
          context={`${tissue} comparison`}
          positiveLabel="Significant"
          negativeLabel="Not significant"
        />
      ),
      size: 150,
    });
  }

  columns.push(expression);
  return columns;
}

function exportFilename(kind, selection) {
  if (kind === 'transmembraneFocus') {
    return `brendo_transmembrane_rra_${new Date().toISOString().slice(0, 10)}.csv`;
  }
  return buildExportFilename(selection);
}

export default function ResultsTable({ kind, rows, selection, onExpression }) {
  const columns = useMemo(
    () => kind === 'transmembraneFocus'
      ? transmembraneFocusColumns(onExpression)
      : allGeneColumns(selection, onExpression),
    [kind, onExpression, selection],
  );
  const filename = exportFilename(kind, selection);

  return (
    <MaterialReactTable
      columns={columns}
      data={rows}
      autoResetPageIndex={false}
      enableColumnPinning
      enableRowSelection
      enableStickyHeader
      positionToolbarAlertBanner="bottom"
      initialState={{
        density: 'compact',
        pagination: { pageIndex: 0, pageSize: 25 },
        columnPinning: { left: [kind === 'transmembraneFocus' ? 'Name' : 'Gene_ID'] },
      }}
      muiTablePaperProps={{ sx: { border: '1px solid', borderColor: 'divider', boxShadow: '0 16px 40px rgba(8,59,75,0.08)' } }}
      muiTableContainerProps={{ sx: { maxHeight: '68vh' } }}
      muiTableHeadCellProps={{ sx: { bgcolor: '#eaf4f6', verticalAlign: 'bottom' } }}
      renderTopToolbarCustomActions={({ table }) => {
        const filteredRows = table.getPrePaginationRowModel().rows.map((row) => row.original);
        const selectedRows = table.getSelectedRowModel().rows.map((row) => row.original);
        return (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ p: 1 }}>
            <Button size="small" startIcon={<FileDownloadOutlinedIcon />} onClick={() => downloadCsv(projectRowsForExport(rows, kind, selection), filename)}>
              Export all
            </Button>
            <Button size="small" startIcon={<FileDownloadOutlinedIcon />} onClick={() => downloadCsv(projectRowsForExport(filteredRows, kind, selection), filename)}>
              Export filtered
            </Button>
            <Button
              size="small"
              startIcon={<FileDownloadOutlinedIcon />}
              disabled={!selectedRows.length}
              onClick={() => downloadCsv(projectRowsForExport(selectedRows, kind, selection), filename)}
            >
              Export selected
            </Button>
          </Stack>
        );
      }}
    />
  );
}
